import { useEffect, useMemo, useState } from "react"
import { translations } from "../../translations"
import type { Album, AlbumData } from "./types"

const LS_KEY = "gallery:albums:v2"
let cache: Album[] | null = null

async function fetchFirst(urls: string[], init?: RequestInit) {
  for (const url of urls) {
    try {
      const r = await fetch(url, init)
      if (r.ok) return await r.json()
    } catch {}
  }
  throw new Error("fail")
}

export function useAlbumsData() {
  const warm = useMemo(() => {
    if (cache) return cache
    try { 
      return JSON.parse(localStorage.getItem(LS_KEY) || "null") as Album[] | null 
    } catch { 
      return null 
    }
  }, [])
  
  const [albums, setAlbums] = useState<Album[]>(warm ?? [])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(!warm)

  useEffect(() => {
    if (warm) return
    
    let cancelled = false
    const controller = new AbortController()
    
    ;(async () => {
      try {
        const albumsData: AlbumData[] = await fetchFirst(
          ["/images/photos/albums.json", "images/photos/albums.json"],
          { cache: "no-cache", signal: controller.signal }
        )
        
        const loadedAlbums: Album[] = albumsData.map((album, albumIdx) => {
          const photos = album.files.map((file, i) => {
            const d = new Date()
            d.setDate(d.getDate() - (albumIdx * 100 + i))
            const basePath = album.folder && album.folder.trim() !== '' 
              ? `/images/photos/${album.folder}` 
              : '/images/photos'
            return { 
              id: `${albumIdx}-${i}`, 
              src: `${basePath}/${file}`, 
              label: file, 
              date: d.toISOString() 
            }
          })
          return {
            id: String(albumIdx),
            name: album.name,
            folder: album.folder,
            coverIndex: album.coverIndex ?? 0,
            photos
          }
        })
        
        if (!cancelled) {
          setAlbums(loadedAlbums)
          cache = loadedAlbums
          setLoading(false)
          try { 
            localStorage.setItem(LS_KEY, JSON.stringify(loadedAlbums)) 
          } catch {}
        }
      } catch (e: any) {
        if (!cancelled && !albums.length && e.name !== 'AbortError') {
          setError(translations("gallery.error_loading"))
          setLoading(false)
        }
      }
    })()
    
    return () => { 
      cancelled = true
      controller.abort()
    }
  }, [warm, albums.length])

  return { albums, error, loading }
}
